import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { ActivatedRoute, Router, UrlTree } from '@angular/router';
import { AbcCore, NavigationInfo } from '@barthauer/abc/core';
import { AbcConfigService } from '@barthauer/abc/core/configuration';
import { ResourceManagerService } from '@barthauer/abc/core/resources';
import { ConvertHelper } from 'libs/abc/core/helpers';
import { from, Observable, of } from 'rxjs';
import { catchError, concatMap, map, mapTo, tap } from 'rxjs/operators';
import { LoginComponentInfo } from '../login/login-component-info';
import {
  AuthenticationLevelMode,
  AuthenticationProviderType,
  IdentityMode,
  LoginInfo,
  PasswordRequirementsInfo,
} from '../models/login-info.model';
import { ILoginSettings } from '../models/login-settings.model';
import { Register } from '../models/register.model';
import { AuthConfigService } from './auth-config.service';
import { LoginSettingsService } from './login-settings.service';
import { WebApiServiceWithAuthConfig } from './webapi-with-auth-config.service';
// TODO @todo import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class UserService extends WebApiServiceWithAuthConfig {
  public static readonly TOKEN_GRANT_TYPE_PASSWORD = 'password';
  public static readonly TOKEN_GRANT_TYPE_REFRESHTOKEN = 'refresh_token';

  private lastLoginSettingsJson: string;
  private lastLoginInfo: LoginInfo;
  private currentAccountLanguage: string;

  constructor(
    httpClient: HttpClient,
    abcConfigService: AbcConfigService,
    authConfigService: AuthConfigService,
    private loginSettingsService: LoginSettingsService,
    private router: Router,
    private route: ActivatedRoute, // TODO @todo private translate: TranslateService,
    private injector: Injector
  ) {
    super(httpClient, abcConfigService, authConfigService);
  }

  public async getSystemName(): Promise<string> {
    const current = await this.authConfigService.current.toPromise();
    return current?.systemName;
  }

  private get API_URL_GetToken(): string {
    return this.abcConfigService.configuration.WebApi.ServiceURLs.Token;
  }

  private get API_URL_Logout(): string {
    return this.abcConfigService.configuration.WebApi.ServiceURLs.Account_Logout;
  }

  private get API_URL_GetUserInfo(): string {
    return this.abcConfigService.configuration.WebApi.ServiceURLs.Account_ReadUserInfo;
  }

  private get API_URL_UpdateIdentity(): string {
    return this.abcConfigService.configuration.WebApi.BaseURL + '/api/Account/UpdateIdentity';
  }

  private get API_URL_SendOneTimePassword(): string {
    return this.abcConfigService.configuration.WebApi.BaseURL + '/api/Account/SendOneTimePassword';
  }

  private get API_URL_Register(): string {
    return this.abcConfigService.configuration.WebApi.ServiceURLs.Account_Register;
  }

  public async getServerVersion(): Promise<string> {
    const cfg = await this.authConfigService.current.toPromise();
    return cfg?.version;
  }

  public loginUser(userName: string, password: string, otp?: string, otpIdentifier?: string): Observable<void> {
    if (userName?.trim() !== userName) {
      userName = userName?.trim();
    }
    const query: any = {
      grant_type: UserService.TOKEN_GRANT_TYPE_PASSWORD,
      userName: userName,
      password: password,
    };

    return this.queryAndStoreLoginSettings(query, true, otp, otpIdentifier);
  }

  public refreshCurrentUser(): Observable<boolean> {
    return this.loginSettingsService.getCurrentLoginSettings().pipe(
      concatMap((oldSettings) => {
        if (oldSettings && (!oldSettings.isLoginRequired || oldSettings.refresh_token)) {
          if (!oldSettings.isLoginRequired) {
            return of(true);
          } else {
            const query: any = {
              grant_type: UserService.TOKEN_GRANT_TYPE_REFRESHTOKEN,
              refresh_token: oldSettings.refresh_token,
            };

            return this.queryAndStoreLoginSettings(query, false).pipe(
              mapTo(true),
              catchError((err) => of(false))
            );
          }
        } else {
          return of(false);
        }
      })
    );
  }

  private queryAndStoreLoginSettings(
    loginQuery: any,
    noRetry: boolean,
    otp?: string,
    otpIdentifier?: string
  ): Observable<void> {
    const encodedRequest = Object.keys(loginQuery)
      .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(loginQuery[key]))
      .join('&');

    let url = this.API_URL_GetToken;
    if (otp && otpIdentifier) {
      url += '?' + otpIdentifier + '=' + otp;
    }
    return this.executeWebApiPOST(url, encodedRequest, true, noRetry).pipe(
      map((response) => this.loginSettingsService.createLoginSettingsFromLoginResponse(response)),
      concatMap((loginSettings) => {
        if (loginSettings.isExpired) {
          throw new Error('Login failed! The received login token is already expired.');
        }
        return this.loginSettingsService.setCurrentLoginSettings(loginSettings);
      })
    );
  }

  public get isCurrentlyLoggedInOrAnonymousAllowed(): Observable<boolean> {
    return this.loginSettingsService.hasCurrentLoginSettings;
  }

  public get usesLocalSecurityContext(): Observable<boolean> {
    return this.loginSettingsService.usesLocalSecurityContext;
  }

  public get canLogout(): Observable<boolean> {
    return this.usesLocalSecurityContext.pipe(map((uses) => !uses));
  }

  public getCurrentLoginInfo(doRefresh: boolean = false): Observable<LoginInfo> {
    return (doRefresh ? this.refreshCurrentUser() : of(true)).pipe(
      concatMap(() =>
        this.loginSettingsService.getCurrentLoginSettings().pipe(
          concatMap((loginSettings) => {
            if (loginSettings) {
              const loginSettingsJson = JSON.stringify(loginSettings);
              if (!doRefresh && this.lastLoginInfo && this.lastLoginSettingsJson === loginSettingsJson) {
                return of(this.lastLoginInfo);
              }
              return this.queryCurrentUserInfo(loginSettings).pipe(
                tap((loginInfo) => {
                  this.lastLoginInfo = loginInfo;
                  this.lastLoginSettingsJson = loginSettingsJson;
                })
              );
            } else {
              return of(null);
            }
          })
        )
      )
    );
  }

  public getCurrentFreshLoginSettings(): Observable<ILoginSettings> {
    return this.refreshCurrentUser().pipe(concatMap(() => this.loginSettingsService.getCurrentLoginSettings()));
  }

  private queryCurrentUserInfo(loginSettings: ILoginSettings): Observable<LoginInfo> {
    return this.executeWebApiPOST(this.API_URL_GetUserInfo, { OutputMode: 'Json' }).pipe(
      map((response) =>
        loginSettings.toLoginInfo(
          <string>response['Id'],
          <string>response['Name'],
          <string>response['DisplayText'],
          ConvertHelper.toBoolean(response['IsAdmin']),
          ConvertHelper.toBoolean(response['NeedsPasswordChange']),
          <string>response['EmailAddress'],
          <AuthenticationLevelMode>response['SecondFactorMode'],
          <IdentityMode>response['EmailIdentityMode'],
          <IdentityMode>response['PhoneIdentityMode'],
          <IdentityMode>response['AppTotpIdentityMode'],
          this.createPasswordRequirements(response['PasswordRequirements'])
        )
      )
    );
  }

  public registerUser(Name: string, Password: string, ConfirmPassword: string): Observable<any> {
    const _queryRegister: Register = {
      Name: Name,
      Password: Password,
      ConfirmPassword: ConfirmPassword,
    };

    return this.executeWebApiPOST(this.API_URL_Register, _queryRegister);
  }

  private createPasswordRequirements(requirements: any): PasswordRequirementsInfo | undefined {
    if (!requirements) {
      return;
    }
    return new PasswordRequirementsInfo(
      this.toNumber(requirements.MinLength),
      this.toNumber(requirements.MaxLength),
      this.toBoolean(requirements.Lower),
      this.toBoolean(requirements.Upper),
      this.toBoolean(requirements.Digit),
      this.toBoolean(requirements.Symbol)
    );
  }

  private toNumber(number: any): number {
    if (number) {
      if (typeof number === 'number') {
        return number;
      } else {
        return parseInt(number);
      }
    }
  }

  private toBoolean(v: any): boolean {
    return ConvertHelper.toBoolean(v);
  }

  public logoutCurrentUser(doNavigateToInitPage?: boolean): Observable<boolean> {
    return this.executeWebApiPOST(this.API_URL_Logout, null).pipe(
      concatMap((result) =>
        this.loginSettingsService
          .removeCurrentLoginSettings()
          .pipe(concatMap((_) => (doNavigateToInitPage ? this.navigateToInitPage() : of(true))))
      )
    );
  }

  public createLoginPageNavigationInfo(router?: Router, route?: ActivatedRoute): NavigationInfo {
    return LoginComponentInfo.createLoginPageNavigationInfo(router ?? this.router, route ?? this.route);
  }

  public navigateToLoginPage(router?: Router, route?: ActivatedRoute): Promise<boolean> {
    return LoginComponentInfo.navigateToLoginPage(router ?? this.router, route ?? this.route);
  }

  public createLoginPageUrlTree(router?: Router, route?: ActivatedRoute): UrlTree {
    return LoginComponentInfo.createLoginPageUrlTree(router ?? this.router, route ?? this.route);
  }

  private navigateToInitPage(router?: Router, route?: ActivatedRoute): Observable<boolean> {
    return this.currentAuthConfiguration.pipe(
      concatMap((cfg) => {
        return from(this.navigateToLoginPage(router, route));
      })
    );
  }

  public async sendOneTimePassword(providerType: AuthenticationProviderType): Promise<void> {
    await this.executeWebApiPOST(this.API_URL_SendOneTimePassword, { ProviderType: providerType }).toPromise();
  }

  public async updateIdentity(
    providerType: AuthenticationProviderType,
    identityValue: string,
    confirmationCode?: string
  ): Promise<string> {
    const data = await this.executeWebApiPOST(this.API_URL_UpdateIdentity, {
      ProviderType: providerType,
      IdentityValue: identityValue,
      ConfirmationCode: confirmationCode,
    }).toPromise();

    return data as unknown as string;
  }

  public async getAccountLanguage(): Promise<string> {
    if (!this.currentAccountLanguage) {
      this.currentAccountLanguage = ConvertHelper.arrayBufferToObject<{ LocaleIdentifier: string }>(
        await this.injector
          .get(AbcCore)
          .data.executeWebApiPOSTMethod(this.abcConfigService.configuration.WebApi.ServiceURLs.Account_GetLanguage, {})
          .toPromise()
      )?.LocaleIdentifier;

      this.injector.get(ResourceManagerService).changeLanguage(this.currentAccountLanguage);
    }
    return this.currentAccountLanguage;
  }

  public async setAccountLanguage(language: string): Promise<boolean> {
    try {
      ConvertHelper.arrayBufferToObject<Object>(
        await this.injector
          .get(AbcCore)
          .data.executeWebApiPOSTMethod(this.abcConfigService.configuration.WebApi.ServiceURLs.Account_SetLanguage, {
            LocaleIdentifier: language,
          })
          .toPromise()
      );
      ResourceManagerService.setCurrentUserLanguage(language);
      return true;
    } catch {
      return false;
    }
  }

  public async setDocumentElementLang(): Promise<void> {
    const accountLanguage = await this.getAccountLanguage();
    if (accountLanguage) {
      document.documentElement.lang = accountLanguage;
    }
  }
}
